import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Clock, Check, AlertCircle, ArrowRight, ArrowLeft, Upload, Download, Play,
  Eye, Star, Zap, BookOpen, Home, Heart, Edit3, RefreshCw, Trash2, Info,
  Camera, Video, ChevronDown, ChevronUp, History, Wifi, FileDown, Truck, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// BackButton component (inline)
const BackButton = ({ to, label = "Back" }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => to ? navigate(to) : navigate(-1)}
      className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-500 transition-colors mb-6 group"
      data-testid="back-button"
    >
      <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
      <span>{label}</span>
    </button>
  );
};

// RushBadge component (inline)
const RushBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded-full border border-red-500/30">
    <Zap size={12} /> RUSH
  </span>
);

// VideoBookBadge component (inline)
const VideoBookBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full border border-purple-500/30">
    <BookOpen size={12} /> VIDEO BOOK
  </span>
);

// VerticalBadge component (inline)
const VerticalBadge = ({ vertical }) => {
  const isRealEstate = vertical === "real_estate";
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
      isRealEstate 
        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    }`}>
      {isRealEstate ? <Home size={12} /> : <Heart size={12} />}
      {isRealEstate ? 'Real Estate' : 'Memorial'}
    </span>
  );
};

// StatusBadge component (inline)
const StatusBadge = ({ status }) => {
  const statusMap = {
    pending_payment: { label: "Pending Payment", class: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" },
    awaiting_upload: { label: "Awaiting Upload", class: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    in_progress: { label: "In Progress", class: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
    review: { label: "Ready for Review", class: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
    completed: { label: "Completed", class: "bg-green-500/20 text-green-400 border-green-500/30" },
  };
  const s = statusMap[status] || { label: status, class: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30" };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.class}`} data-testid="status-badge">
      {s.label}
    </span>
  );
};

// RushDeadlineCountdown component (inline)
const RushDeadlineCountdown = ({ timeline }) => {
  if (!timeline?.is_rush || !timeline?.rush_deadline) return null;
  
  const isOverdue = timeline.rush_deadline_passed;
  const hoursRemaining = timeline.rush_hours_remaining || 0;
  
  return (
    <div className={`rounded-lg p-3 ${isOverdue ? 'bg-red-500/20 border border-red-500/50' : 'bg-orange-500/20 border border-orange-500/50'}`}>
      <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-400' : 'text-orange-400'}`}>
        <Clock size={16} />
        <span className="font-semibold">
          {isOverdue ? 'RUSH DEADLINE OVERDUE' : '48hr RUSH Deadline'}
        </span>
      </div>
      <div className="text-sm mt-1">
        {isOverdue ? (
          <span className="text-red-300">Deadline was: {timeline.rush_deadline_display}</span>
        ) : (
          <>
            <span className={hoursRemaining < 12 ? 'text-red-400 font-bold' : 'text-orange-300'}>
              {hoursRemaining < 1 
                ? `${Math.round(hoursRemaining * 60)} minutes remaining`
                : `${Math.round(hoursRemaining)} hours remaining`
              }
            </span>
            <span className="text-zinc-400 ml-2">• Due: {timeline.rush_deadline_display}</span>
          </>
        )}
      </div>
    </div>
  );
};

// This component receives useAuth as a prop from App.js
const OrderDetailPage = ({ useAuth }) => {
  const { user } = useAuth();
  const { id } = useParams();  // Extract order ID from URL params
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [photoUrls, setPhotoUrls] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [reEditNotes, setReEditNotes] = useState("");
  const [extendDays, setExtendDays] = useState(7);
  const [extending, setExtending] = useState(false);
  const [requestingReEdit, setRequestingReEdit] = useState(false);
  const [uploadConfirmed, setUploadConfirmed] = useState(false);
  const [reEditMsg, setReEditMsg] = useState("");
  const [reEditSubmitting, setReEditSubmitting] = useState(false);
  const [reEditSent, setReEditSent] = useState(false);
  const [reEditStatus, setReEditStatus] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    
    // Fetch order data
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/${id}`);
        if (!cancelled) setOrder(res.data);
      } catch (err) {
        console.error("Failed to fetch order:", err);
        if (!cancelled) setOrder(null);
      }
      if (!cancelled) setLoading(false);
    })();
    
    // Check if returning from paid re-edit purchase
    const reEditPurchased = searchParams.get('re_edit_purchased');
    const sessionId = searchParams.get('session_id');
    if (reEditPurchased === 'true' && sessionId) {
      (async () => {
        try {
          await axios.post(`${API}/orders/${id}/re-edit/confirm?session_id=${sessionId}`);
          // Refetch order after confirming
          const res = await axios.get(`${API}/orders/${id}`);
          if (!cancelled) setOrder(res.data);
          alert("Paid re-edit confirmed! Our editors will process your changes.");
          // Clear URL params
          window.history.replaceState({}, '', `/orders/${id}`);
        } catch (err) {
          console.error("Failed to confirm re-edit:", err);
        }
      })();
    }
    
    return () => { cancelled = true; };
  }, [id, searchParams]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}`);
      setOrder(res.data);
      // Fetch RE re-edit status (additive, non-blocking)
      if (res.data?.vertical === "real_estate") {
        try {
          const reRes = await axios.get(`${API}/re-reedit/status/${id}`);
          setReEditStatus(reRes.data);
          if (reRes.data?.status === "open") setReEditSent(true);
        } catch { /* ignore */ }
      }
    } catch (err) {
      console.error("Failed to fetch order:", err);
      setOrder(null);
    }
    setLoading(false);
  };

  const handleUploadPhotos = async () => {
    if (!photoUrls.trim()) return;
    
    const urls = photoUrls.split("\n").filter(u => u.trim());
    if (urls.length === 0) return;

    // Check limit
    const currentCount = order.photos?.length || 0;
    if (currentCount + urls.length > order.photo_limit) {
      setUploadError(`Cannot add ${urls.length} photos. Limit: ${order.photo_limit}, Current: ${currentCount}`);
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      await axios.post(`${API}/orders/${id}/photos`, { photo_urls: urls });
      setPhotoUrls("");
      fetchOrder();
      setFailedAttempts(0);
    } catch (err) {
      setFailedAttempts(f => f + 1);
      setUploadError(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const handleConfirmUpload = async () => {
    try {
      const res = await axios.post(`${API}/orders/${id}/confirm-upload`);
      fetchOrder();
      setUploadConfirmed(true);
      
      // Auto-retry editor email if it failed during confirm
      if (res.data && res.data.editor_email_sent === false) {
        try { await axios.post(`${API}/orders/${id}/retry-editor-email`); } catch {}
      }
    } catch (err) {
      setUploadError(err.response?.data?.detail || "Failed to confirm upload");
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/orders/${id}/photos/${photoId}`);
      fetchOrder();
    } catch (err) {
      console.error("Failed to delete photo:", err);
    }
  };

  // Handle file upload for adding more photos (customer portal)
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const remaining = order.photo_limit - (order.photos?.length || 0);
    if (files.length > remaining) {
      setUploadError(`You can only upload ${remaining} more photo${remaining !== 1 ? 's' : ''}`);
      return;
    }
    
    setUploading(true);
    setUploadError("");
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      await axios.post(`${API}/orders/${order.id}/upload-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      fetchOrder();
      e.target.value = '';
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadError(err.response?.data?.detail || "Failed to upload photos. Please try again.");
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handleUploadVideo = async () => {
    if (!videoUrl.trim()) return;
    try {
      await axios.post(`${API}/orders/${id}/video`, { video_url: videoUrl });
      setVideoUrl("");
      fetchOrder();
    } catch (err) {
      console.error("Failed to upload video:", err);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/orders/${id}/approve`);
      fetchOrder();
    } catch (err) {
      console.error("Failed to approve:", err);
    }
  };

  const handleRequestFreeReEdit = async () => {
    if (!reEditNotes.trim()) return;
    setRequestingReEdit(true);
    try {
      await axios.post(`${API}/orders/${id}/re-edit`, { notes: reEditNotes });
      setReEditNotes("");
      fetchOrder();
      alert("Re-edit request submitted! Our editors will make the requested changes.");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit re-edit request");
    }
    setRequestingReEdit(false);
  };

  const handlePurchasePaidReEdit = async () => {
    if (!reEditNotes.trim()) return;
    setRequestingReEdit(true);
    try {
      const res = await axios.post(`${API}/orders/${id}/re-edit/purchase`, { 
        notes: reEditNotes,
        origin_url: window.location.origin
      });
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create re-edit purchase");
      setRequestingReEdit(false);
    }
  };

  const handleExtendRetention = async () => {
    if (extendDays < 1 || extendDays > 30) return;
    setExtending(true);
    try {
      await axios.post(`${API}/admin/orders/${id}/extend-retention`, { 
        extend_days: extendDays,
        reason: "Extended via admin dashboard"
      });
      fetchOrder();
      alert(`Retention extended by ${extendDays} days`);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to extend retention");
    }
    setExtending(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Order Not Found</h2>
          <p className="text-zinc-400 mb-6">This order doesn&apos;t exist or you don&apos;t have access to it.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="btn-primary"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  const statusSteps = ["payment_confirmed", "awaiting_upload", "in_progress", "review", "completed"];
  const currentStepIndex = statusSteps.indexOf(order.status);
  const timeline = order.timeline || {};
  const milestones = timeline.milestones || [];
  const isRush = timeline.is_rush;
  
  // Determine video type based on order vertical
  const isRealEstateOrder = order.vertical === "real_estate";
  const videoType = isRealEstateOrder ? "property video" : "memorial video";
  const accentColor = isRealEstateOrder ? "emerald" : "yellow";

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="order-detail-page">
      <div className="max-w-4xl mx-auto">
        <BackButton to="/dashboard" label="Back to Dashboard" />
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold font-serif">{order.order_number}</h1>
              <VerticalBadge vertical={order.vertical} />
              {isRush && <RushBadge />}
              {order.video_book && <VideoBookBadge />}
            </div>
            <p className="text-zinc-400">{order.package_name}</p>
          </div>
          <StatusBadge status={order.status} />
        </div>

        {/* Pending Payment CTA */}
        {order.status === "pending_payment" && (
          <div className="card-dark p-6 mb-6 border-2 border-yellow-500/50 bg-yellow-500/5" data-testid="pending-payment-cta">
            <p className="text-zinc-300 mb-4">This order has not been completed. Please finish payment to begin production.</p>
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/checkout/resume/${order.id}`);
                  window.location.href = res.data.checkout_url;
                } catch (err) {
                  alert(err.response?.data?.detail || "Failed to resume payment");
                }
              }}
              className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 px-6 rounded-lg text-lg transition"
              data-testid="complete-payment-btn"
            >
              Complete Payment — ${order.total_amount?.toLocaleString()}
            </button>
          </div>
        )}

        {/* Production Timeline */}
        <div className="card-dark p-4 sm:p-6 mb-4 sm:mb-6" data-testid="production-timeline">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-sm sm:text-base">Production Timeline</h2>
            {timeline.timezone && (
              <span className="text-xs text-zinc-500 hidden sm:inline">Timezone: {timeline.timezone}</span>
            )}
          </div>
          
          {/* RUSH Order Deadline */}
          {isRush && order.status !== "completed" && (
            <RushDeadlineCountdown timeline={timeline} />
          )}
          
          {/* Timeline Visualization */}
          {milestones.length > 0 && (
            <div className={`relative ${isRush ? 'mt-4' : ''}`}>
              {/* Progress Bar Background */}
              <div className="absolute top-4 left-0 right-0 h-1 bg-zinc-700 rounded"></div>
              
              {/* Progress Bar Fill */}
              <div 
                className={`absolute top-4 left-0 h-1 rounded transition-all duration-500 ${
                  isRush 
                    ? (timeline.rush_deadline_passed ? 'bg-red-500' : 'bg-orange-500')
                    : 'bg-yellow-500'
                }`}
                style={{ 
                  width: timeline.is_early_complete || order.status === "completed" 
                    ? '100%' 
                    : isRush
                      ? `${Math.min(100, (timeline.hours_elapsed / 48) * 100)}%`
                      : `${Math.min(100, (timeline.days_elapsed / 5) * 100)}%` 
                }}
              ></div>
              
              {/* Milestones */}
              <div className="flex justify-between relative">
                {milestones.map((milestone, i) => (
                  <div key={i} className="flex flex-col items-center" style={{ width: isRush ? '50%' : '25%' }}>
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center z-10 text-xs ${
                      milestone.is_completed || order.status === "completed"
                        ? 'bg-green-500 text-white' 
                        : milestone.is_current 
                          ? (isRush ? 'bg-orange-500 text-white animate-pulse' : 'bg-yellow-500 text-black animate-pulse')
                          : 'bg-zinc-700 text-zinc-400'
                    }`}>
                      {milestone.is_completed || order.status === "completed" ? (
                        <Check size={14} />
                      ) : isRush ? (
                        <Zap size={12} />
                      ) : (
                        <span className="text-[10px] sm:text-xs font-bold">D{milestone.day}</span>
                      )}
                    </div>
                    <div className={`mt-1.5 sm:mt-2 text-center px-0.5 ${
                      milestone.is_current 
                        ? (isRush ? 'text-orange-500' : 'text-yellow-500')
                        : milestone.is_completed ? 'text-white' : 'text-zinc-500'
                    }`}>
                      <div className="text-[10px] sm:text-sm font-medium leading-tight">{milestone.label}</div>
                      <div className="text-[9px] sm:text-xs">{milestone.date}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Early Completion Badge */}
              {timeline.is_early_complete && (
                <div className="mt-4 flex items-center justify-center gap-2 text-green-500">
                  <Zap size={16} />
                  <span className="text-sm font-medium">Completed Early!</span>
                </div>
              )}
              
              {/* Progress Info */}
              {timeline.start_date && order.status !== "completed" && (
                <div className="mt-4 text-center text-sm text-zinc-400">
                  {isRush ? (
                    timeline.rush_hours_remaining > 0 
                      ? `${Math.round(timeline.hours_elapsed)} hours elapsed • ${Math.round(timeline.rush_hours_remaining)} hours remaining`
                      : `48hr deadline passed`
                  ) : (
                    `Day ${timeline.days_elapsed} of production`
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Fallback Simple Timeline (no milestones) */}
          {milestones.length === 0 && (
            <div className="flex justify-between">
              {["Awaiting Upload", "In Progress", "Review", "Completed"].map((label, i) => (
                <div key={i} className="flex-1 text-center">
                  <div className={`w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center ${
                    i <= currentStepIndex ? 'bg-yellow-500 text-black' : 'bg-zinc-700'
                  }`}>
                    {i < currentStepIndex ? <Check size={16} /> : i + 1}
                  </div>
                  <div className={`text-xs ${i <= currentStepIndex ? 'text-white' : 'text-zinc-500'}`}>{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PROMINENT UPLOAD PHOTOS SECTION - Next Step After Payment */}
        {(order.status === "payment_confirmed" || order.status === "awaiting_upload") && (
          <div className="mb-6" data-testid="upload-section-hero">
            {/* Action Required Banner */}
            <div className={`bg-gradient-to-r ${isRealEstateOrder ? 'from-emerald-500 to-emerald-400' : 'from-yellow-500 to-amber-500'} text-black px-4 py-3 rounded-t-xl flex items-center justify-center gap-2`}>
              <div className="w-6 h-6 bg-black/20 rounded-full flex items-center justify-center animate-pulse">
                <span className="text-sm font-bold">1</span>
              </div>
              <span className="font-bold text-lg">ACTION REQUIRED: Upload Your Photos</span>
            </div>
            
            <div className={`bg-zinc-900 border-2 border-${accentColor}-500/50 border-t-0 rounded-b-xl p-6`}>
              <p className="text-center text-zinc-300 mb-4">
                Your payment is confirmed! Now upload your photos to begin creating your {videoType}.
              </p>
              
              {/* Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-400">Photos uploaded</span>
                  <span className={`font-semibold ${order.photos?.length >= order.photo_limit ? 'text-green-500' : `text-${accentColor}-500`}`}>
                    {order.photos?.length || 0} / {order.photo_limit}
                  </span>
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-3">
                  <div 
                    className={`bg-gradient-to-r ${isRealEstateOrder ? 'from-emerald-500 to-emerald-400' : 'from-yellow-500 to-amber-500'} h-3 rounded-full transition-all`}
                    style={{ width: `${Math.min(100, ((order.photos?.length || 0) / order.photo_limit) * 100)}%` }}
                  ></div>
                </div>
              </div>

              {uploadError && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-3 rounded-lg mb-4">
                  {uploadError}
                </div>
              )}

              {/* Upload Area */}
              <label 
                htmlFor="photo-file-input-hero"
                className="flex flex-col items-center justify-center w-full h-44 border-2 border-dashed border-yellow-500/50 rounded-xl cursor-pointer hover:border-yellow-500 hover:bg-yellow-500/5 transition-all bg-zinc-800/50"
              >
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                    <Camera className="w-8 h-8 text-yellow-500" />
                  </div>
                  <p className="mb-2 text-xl font-semibold text-white">
                    Tap to Select Photos
                  </p>
                  <p className="text-sm text-zinc-400">
                    JPG, PNG, WebP • Select multiple at once
                  </p>
                </div>
                <input 
                  id="photo-file-input-hero"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    
                    const remaining = order.photo_limit - (order.photos?.length || 0);
                    if (files.length > remaining) {
                      setUploadError(`You can only upload ${remaining} more photo${remaining !== 1 ? 's' : ''}`);
                      return;
                    }
                    
                    setUploading(true);
                    setUploadError("");
                    setUploadProgress(0);
                    
                    let uploaded = 0;
                    let lastErr = null;
                    
                    // Upload files one at a time to avoid proxy size limits
                    for (const file of files) {
                      try {
                        const formData = new FormData();
                        formData.append('files', file);
                        
                        await axios.post(`${API}/orders/${order.id}/upload-files`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                          timeout: 60000,
                        });
                        uploaded++;
                        setUploadProgress(Math.round((uploaded / files.length) * 100));
                      } catch (err) {
                        console.error(`Upload failed for ${file.name}:`, err);
                        lastErr = err;
                      }
                    }
                    
                    if (uploaded > 0) fetchOrder();
                    if (lastErr && uploaded < files.length) {
                      setUploadError(`Uploaded ${uploaded}/${files.length} photos. Some failed — try again for the rest.`);
                      setFailedAttempts(prev => prev + 1);
                    }
                    
                    e.target.value = '';
                    setUploading(false);
                    setUploadProgress(0);
                  }}
                  data-testid="photo-file-input-hero"
                />
              </label>
              
              {/* Upload Progress */}
              {uploading && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-yellow-500">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="font-medium">Uploading photos...</span>
                    </div>
                    <span className="font-bold text-lg">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-zinc-700 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-yellow-500 to-amber-500 h-full rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {failedAttempts >= 3 && (
                <div className="bg-blue-500/20 border border-blue-500/50 p-3 rounded-lg mt-4">
                  <div className="flex items-center gap-2 text-blue-400 mb-1">
                    <Wifi size={16} />
                    <span className="font-medium">Upload Tip</span>
                  </div>
                  <p className="text-sm text-blue-300">
                    Try connecting to a stable WiFi network. If problems persist, contact{" "}
                    <a href="mailto:izzopro@izzopro.com" className="underline">izzopro@izzopro.com</a>
                  </p>
                </div>
              )}

              {/* Review & Confirm Section */}
              {order.photos?.length > 0 && !uploading && (
                <div className="mt-6 border-t border-zinc-700 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Eye size={18} className={`text-${accentColor}-500`} />
                      Review Your Photos
                    </h3>
                    <span className={`text-${accentColor}-400 font-bold text-lg`}>
                      {order.photos.length} / {order.photo_limit} photos
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm mb-4">
                    Review your photos below. You can remove any photo or add more before submitting. Once you confirm, your photos will be sent to our editors.
                  </p>
                  
                  {/* Photo Grid Preview */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-5 max-h-64 overflow-y-auto rounded-lg p-1">
                    {order.photos.map((photo) => (
                      <div key={photo.id} className="relative group aspect-square rounded-lg overflow-hidden bg-zinc-800">
                        <img src={`${BACKEND_URL}${photo.url}`} alt={photo.filename} className="w-full h-full object-cover" />
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm("Remove this photo?")) {
                              try {
                                await axios.delete(`${API}/orders/${order.id}/photos/${photo.id}`);
                                fetchOrder();
                              } catch {}
                            }
                          }}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove photo"
                        >
                          <X size={12} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Final Confirm Button */}
                  <button 
                    onClick={handleConfirmUpload}
                    disabled={order.photos.length === 0}
                    className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-40 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 text-lg transition-all shadow-lg shadow-green-500/20"
                    data-testid="confirm-upload-btn-hero"
                  >
                    <Check size={24} /> Confirm & Submit {order.photos.length} Photos to Editor
                  </button>
                  <p className="text-center text-zinc-500 text-xs mt-2">
                    This cannot be undone. Your editor will begin work after submission.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PROMINENT PHOTOS SUBMITTED CONFIRMATION - At Top */}
        {(uploadConfirmed || order.status === "upload_confirmed" || order.status === "in_production" || order.status === "editing" || order.status === "in_progress") && order.status !== "completed" && order.status !== "review" && (
          <div className="mb-6" data-testid="upload-confirmation-hero">
            {/* Success Banner */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-t-xl flex items-center justify-center gap-2">
              <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                <Check size={16} />
              </div>
              <span className="font-bold text-lg">PHOTOS SUBMITTED SUCCESSFULLY!</span>
            </div>
            
            <div className="bg-zinc-900 border-2 border-green-500/50 border-t-0 rounded-b-xl p-6 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-500" size={32} />
              </div>
              <p className="text-zinc-300 mb-4 text-lg">
                Your photos have been received! Our editors are now creating your {videoType}.
              </p>
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-4 text-left max-w-md mx-auto">
                <h3 className={`font-semibold text-${accentColor}-500 mb-2`}>📍 What Happens Next?</h3>
                <ul className="text-sm text-zinc-300 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Our editors will craft your {videoType}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className={`text-${accentColor}-500`}>→</span>
                    <span>You&apos;ll receive email updates on progress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className={`text-${accentColor}-500`}>→</span>
                    <span>Track status anytime on this page</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-zinc-500">
                Questions? Contact <a href="mailto:izzopro@izzopro.com" className={`text-${accentColor}-500 hover:underline`}>izzopro@izzopro.com</a>
              </p>
            </div>
          </div>
        )}

        {/* PROMINENT VIDEO READY DOWNLOAD SECTION - Right after Timeline */}
        {order.status === "completed" && order.videos?.length > 0 && !order.video_expired && (
          <div className="mb-6" data-testid="video-ready-section">
            {/* Success Banner */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-t-xl flex items-center justify-center gap-2">
              <div className="text-2xl">🎬</div>
              <span className="font-bold text-lg">YOUR VIDEO IS READY!</span>
            </div>
            
            <div className="bg-zinc-900 border-2 border-green-500/50 border-t-0 rounded-b-xl p-6">
              <p className="text-center text-zinc-300 mb-4">
                Your {videoType} has been completed. Click below to download.
              </p>
              
              {order.days_until_deletion !== undefined && (
                <div className="text-center mb-4">
                  <span className={`inline-flex items-center gap-2 bg-${accentColor}-500/20 text-${accentColor}-400 px-4 py-2 rounded-full text-sm`}>
                    <Clock size={16} />
                    Available for {order.days_until_deletion} more days
                  </span>
                </div>
              )}
              
              {/* Download + Re-Edit side by side */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/orders/${order.id}/track-download`);
                    } catch (e) {
                      console.error("Failed to track download:", e);
                    }
                    window.location.href = `${BACKEND_URL}/api/orders/${order.id}/download-video`;
                  }}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-4 rounded-xl flex items-center justify-center gap-3 text-lg shadow-lg shadow-green-500/30 transition-all hover:scale-105"
                  data-testid="download-video-hero-btn"
                >
                  <Download size={24} /> Download Video
                </button>
                {isRealEstateOrder && !reEditSent && (!reEditStatus?.hasRequest || reEditStatus?.status === "completed" || reEditStatus?.status === "rejected") && (
                  <button
                    onClick={() => document.getElementById('re-edit-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="sm:w-auto bg-zinc-800 hover:bg-zinc-700 text-emerald-400 font-semibold px-6 py-4 rounded-xl flex items-center justify-center gap-2 border border-emerald-500/30 transition-all"
                    data-testid="re-edit-scroll-btn"
                  >
                    <Edit3 size={18} /> Request Re-Edit
                  </button>
                )}
              </div>

              {/* Inline Re-Edit form (RE only) */}
              {isRealEstateOrder && !reEditSent && (!reEditStatus?.hasRequest || reEditStatus?.status === "completed" || reEditStatus?.status === "rejected") && (
                <div id="re-edit-form" className="border-t border-zinc-700 pt-4 mt-2" data-testid="re-edit-section">
                  <p className="text-zinc-400 text-sm mb-3">
                    <Edit3 size={14} className="inline text-emerald-500 mr-1" />
                    Not satisfied? Describe changes and our editor will revise your video.
                  </p>
                  <textarea
                    value={reEditMsg}
                    onChange={(e) => setReEditMsg(e.target.value)}
                    placeholder="e.g., different music, adjust timing, add/remove photos..."
                    rows={2}
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 text-white placeholder-zinc-500 text-sm mb-3 focus:border-emerald-500 focus:outline-none"
                    data-testid="re-edit-message"
                  />
                  <button
                    onClick={async () => {
                      if (!reEditMsg.trim()) return;
                      setReEditSubmitting(true);
                      try {
                        await axios.post(`${API}/re-reedit`, { order_id: order.id, message: reEditMsg });
                        setReEditSent(true);
                        fetchOrder();
                      } catch (e) {
                        alert(e.response?.data?.detail || "Failed to submit re-edit request");
                      }
                      setReEditSubmitting(false);
                    }}
                    disabled={reEditSubmitting || !reEditMsg.trim()}
                    className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-semibold px-6 py-2.5 rounded-lg text-sm transition"
                    data-testid="re-edit-submit"
                  >
                    {reEditSubmitting ? "Sending..." : "Submit Re-Edit Request"}
                  </button>
                </div>
              )}

              {/* Re-Edit Status (inline) */}
              {reEditSent && reEditStatus?.hasRequest && (
                <div className="border-t border-zinc-700 pt-4 mt-2" data-testid="re-edit-status">
                  {reEditStatus.status === "open" && (
                    <p className="text-emerald-400 text-sm flex items-center gap-2"><RefreshCw size={14} className="animate-spin" style={{ animationDuration: '3s' }} /> Re-edit request submitted — waiting on editor</p>
                  )}
                  {reEditStatus.status === "in_progress" && (
                    <p className="text-yellow-400 text-sm flex items-center gap-2"><Edit3 size={14} /> Editor is reviewing your request</p>
                  )}
                  {reEditStatus.status === "completed" && (
                    <p className="text-green-400 text-sm flex items-center gap-2"><Check size={14} /> Re-edit completed — refresh to see updated version</p>
                  )}
                  {reEditStatus.status === "rejected" && (
                    <p className="text-red-400 text-sm flex items-center gap-2"><AlertCircle size={14} /> Re-edit declined — please contact support</p>
                  )}
                  {reEditStatus.submitted_at && (
                    <p className="text-zinc-600 text-xs mt-1">Submitted: {new Date(reEditStatus.submitted_at).toLocaleString()}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="card-dark p-6">
            <h2 className="font-semibold mb-4">Order Details</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-400">Package</span><span>{order.package_name}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Photo Limit</span><span>{order.photo_limit}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Total</span><span className="text-yellow-500 font-bold">${order.total_amount?.toLocaleString()}</span></div>
              {order.upsells?.includes("rush_delivery") && (
                <div className="flex justify-between"><span className="text-zinc-400">Rush Delivery</span><span className="text-green-500">✓</span></div>
              )}
              {order.upsells?.includes("song_request") && (
                <div className="flex justify-between"><span className="text-zinc-400">Song Request</span><span>{order.song_details || "✓"}</span></div>
              )}
              {order.video_book && (
                <div className="flex justify-between"><span className="text-zinc-400">Premium Video Book</span><span className="text-yellow-500">✓ +${order.video_book_price || 500}</span></div>
              )}
              {order.video_book && order.video_book_shipping && (
                <div className="flex justify-between"><span className="text-zinc-400">Shipping</span><span className="text-zinc-300">+${order.video_book_shipping}</span></div>
              )}
            </div>
            {/* Invoice Download Button */}
            {order.payment_status === "paid" && (
              <a
                href={`${BACKEND_URL}/api/orders/${order.id}/invoice`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-2 w-full bg-zinc-700 hover:bg-zinc-600 text-white py-2 px-4 rounded-lg transition-colors text-sm"
                data-testid="download-invoice-btn"
              >
                <FileDown size={16} />
                Download Invoice
              </a>
            )}
          </div>
          <div className="card-dark p-6">
            <h2 className="font-semibold mb-4">Customer Info</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-400">Name</span><span>{order.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Email</span><span>{order.customer_email}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Phone</span><span>{order.customer_phone}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Timezone</span><span>{order.timezone}</span></div>
            </div>
            {/* Shipping Address for Video Book */}
            {order.video_book && order.shipping_address && (
              <div className="mt-4 pt-4 border-t border-zinc-700">
                <h3 className="text-sm font-semibold text-yellow-500 mb-2 flex items-center gap-2">
                  <Truck size={14} />
                  Video Book Delivery Address
                </h3>
                <div className="text-sm text-zinc-300">
                  <p>{order.shipping_address.street}</p>
                  <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
                  <p>{order.shipping_address.country}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Old Video Ready section removed - now using prominent hero section after timeline */}

        {/* Deletion Warning */}
        {order.status === "completed" && order.days_until_deletion !== undefined && !order.video_expired && (
          <div className={`${order.days_until_deletion <= 1 ? 'bg-red-500/20 border-red-500/50' : order.days_until_deletion <= 4 ? 'bg-orange-500/20 border-orange-500/50' : 'bg-yellow-500/20 border-yellow-500/50'} border rounded-lg p-4 mb-6`}>
            <div className={`flex items-center gap-2 ${order.days_until_deletion <= 1 ? 'text-red-400' : order.days_until_deletion <= 4 ? 'text-orange-400' : 'text-yellow-400'}`}>
              <Clock size={20} />
              <span className="font-semibold">
                {order.days_until_deletion <= 0 
                  ? "⚠️ Video expires TODAY!" 
                  : order.days_until_deletion === 1 
                    ? "⚠️ Video expires TOMORROW!" 
                    : `Video will be deleted in ${order.days_until_deletion} days`}
              </span>
            </div>
            <p className={`text-sm mt-1 ${order.days_until_deletion <= 1 ? 'text-red-300/80' : order.days_until_deletion <= 4 ? 'text-orange-300/80' : 'text-yellow-300/80'}`}>
              {order.days_until_deletion <= 1 
                ? "Download your video immediately before it's permanently removed!"
                : "Please download your video before it's automatically removed."}
            </p>
          </div>
        )}

        {/* Video Expired Notice */}
        {order.video_expired && (
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-6 mb-6 text-center">
            <div className="text-4xl mb-3">📼</div>
            <h3 className="text-xl font-semibold text-zinc-300 mb-2">Video No Longer Available</h3>
            <p className="text-zinc-400 mb-4">
              This video has expired and is no longer available for download.
              Videos are automatically removed 14 days after completion.
            </p>
            <p className="text-sm text-zinc-500">
              Need assistance? Contact <a href="mailto:izzopro@izzopro.com" className="text-yellow-500 hover:underline">izzopro@izzopro.com</a>
            </p>
          </div>
        )}

        {/* Old Upload Section removed - now using prominent hero section at top */}

        {/* Old Upload Confirmation Message removed - now using prominent hero section at top */}

        {/* Photos Grid */}
        {order.photos?.length > 0 && (
          <div className="card-dark p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold">Uploaded Photos ({order.photos.length}/{order.photo_limit})</h2>
              {/* Download All Photos - Editor/Admin only (NOT customers) */}
              {user && (user.role === "editor" || user.role === "admin") && (
                <div className="flex items-center gap-2">
                  {isRealEstateOrder && (
                    <a
                      href={`${BACKEND_URL}/api/editor/real-estate/orders/${order.id}/photos.zip`}
                      className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                      data-testid="re-download-photos-zip"
                    >
                      <FileDown size={16} /> Download All Photos (ZIP)
                    </a>
                  )}
                  {!isRealEstateOrder && (
                    <a
                      href={`${API}/orders/${order.id}/download-all-photos`}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                      data-testid="download-all-photos-btn"
                    >
                      <FileDown size={16} /> Download All Photos (ZIP)
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {order.photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img 
                    src={photo.url} 
                    alt="Uploaded" 
                    className="w-full h-32 object-cover rounded-lg"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/200?text=Image'; }}
                  />
                  {order.status === "awaiting_upload" && user?.role === "customer" && (
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="absolute top-2 right-2 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Add More Photos - For customers who haven't hit their limit */}
            {user?.role === "customer" && order.photos.length < order.photo_limit && order.status !== "completed" && (
              <div className="mt-4 pt-4 border-t border-zinc-700">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm text-zinc-400">
                      You can upload <span className="text-yellow-500 font-semibold">{order.photo_limit - order.photos.length} more photos</span>
                    </p>
                    <p className="text-xs text-zinc-500">Package limit: {order.photo_limit} photos</p>
                  </div>
                  <button
                    onClick={() => document.getElementById('add-more-photos-input').click()}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                    disabled={uploading}
                    data-testid="add-more-photos-btn"
                  >
                    <Upload size={16} /> Add More Photos
                  </button>
                </div>
                <input
                  id="add-more-photos-input"
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
                {uploading && (
                  <div className="mt-2">
                    <div className="w-full bg-zinc-700 rounded-full h-2">
                      <div 
                        className="bg-yellow-500 h-2 rounded-full transition-all" 
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1 text-center">{uploadProgress}% uploaded</p>
                  </div>
                )}
                {uploadError && (
                  <p className="text-red-400 text-sm mt-2">{uploadError}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Editor Upload Video - ONLY for editors/admins on Memorial orders */}
        {!isRealEstateOrder && (order.status === "in_progress" || order.status === "review") && user && (user.role === "editor" || user.role === "admin") && (
          <div className="card-dark p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="font-semibold mb-4">Upload Video</h2>
            {order.re_edit_requested && (
              <div className="bg-orange-500/20 border border-orange-500/50 p-3 rounded-lg mb-4">
                <div className="font-medium text-orange-400 mb-1">Re-edit Requested</div>
                <p className="text-sm">{order.re_edit_notes}</p>
              </div>
            )}
            <div className="flex gap-4">
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="Video URL..."
                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 focus:outline-none focus:border-yellow-500"
                data-testid="video-url-input"
              />
              <button 
                onClick={handleUploadVideo}
                disabled={!videoUrl.trim()}
                className="btn-primary"
                data-testid="upload-video-btn"
              >
                Upload Video
              </button>
            </div>
          </div>
        )}

        {/* Videos */}
        {order.videos?.length > 0 && (
          <div className="card-dark p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="font-semibold mb-4">Your {isRealEstateOrder ? 'Property' : 'Memorial'} Video</h2>
            {order.videos.map((video, i) => (
              <div key={video.id} className="mb-4 last:mb-0">
                <video 
                  src={`${BACKEND_URL}/api/orders/${order.id}/stream-video`}
                  controls 
                  className="w-full rounded-lg"
                  data-testid="video-player"
                  preload="metadata"
                />
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={async () => {
                      // Track download then redirect to thank you page
                      try {
                        await axios.post(`${API}/orders/${order.id}/track-download`);
                      } catch (e) {
                        console.error("Failed to track download:", e);
                      }
                      // Use the authenticated download endpoint
                      window.location.href = `${BACKEND_URL}/api/orders/${order.id}/download-video`;
                    }}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-3 rounded-lg flex items-center gap-2 transition"
                    data-testid="download-video-btn"
                  >
                    <Download size={18} /> Download Video
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Customer Review Actions */}
        {order.status === "review" && (user?.role === "customer" || (user?.id === order.user_id)) && (
          <div className="card-dark p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="font-semibold mb-4">Review Your Video</h2>
            <p className="text-zinc-400 mb-4">
              Please review your {videoType}. You can approve it or request changes.
            </p>
            <div className="flex flex-col md:flex-row gap-4">
              <button onClick={handleApprove} className="btn-primary" data-testid="approve-video-btn">
                <Check className="inline mr-2" size={18} /> Approve Video
              </button>
            </div>
          </div>
        )}

        {/* Re-Edit In Progress Notice */}
        {order.re_edit_in_progress && (
          <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-purple-400">
              <RefreshCw size={20} className="animate-spin" />
              <span className="font-semibold">Re-Edit In Progress</span>
            </div>
            <p className="text-sm text-purple-300/80 mt-1">
              Our editors are working on your requested changes. You&apos;ll be notified when the revised video is ready.
            </p>
            {order.re_edit_notes && (
              <div className="mt-3 bg-zinc-800/50 rounded p-3">
                <p className="text-xs text-zinc-500 mb-1">Your requested changes:</p>
                <p className="text-sm text-zinc-300">{order.re_edit_notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Re-Edit Request Section (Completed orders within 7-day window) */}
        {order.status === "completed" && order.re_edit_window_active && !order.re_edit_in_progress && (user?.role === "customer" || (user?.id === order.user_id)) && (
          <div className="card-dark p-6 mb-6 border border-zinc-700" data-testid="re-edit-section">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <RefreshCw size={18} className="text-yellow-500" />
              Request Changes
            </h2>
            
            {/* Time Remaining */}
            <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">Re-edit window expires in:</span>
                <span className={`font-semibold ${order.re_edit_window_days_left <= 1 ? 'text-red-400' : order.re_edit_window_days_left <= 3 ? 'text-orange-400' : 'text-green-400'}`}>
                  {order.re_edit_window_days_left} day{order.re_edit_window_days_left !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Free Re-Edit */}
            {order.can_request_free_re_edit && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-semibold">FREE</span>
                  <span className="text-sm text-zinc-300">One complimentary re-edit included</span>
                </div>
                <textarea
                  value={reEditNotes}
                  onChange={(e) => setReEditNotes(e.target.value)}
                  placeholder="Describe the changes you'd like (e.g., 'Please add more photos at the beginning' or 'Change the music timing at 1:30')..."
                  className="w-full h-24 bg-zinc-800 border border-zinc-700 rounded-lg p-3 focus:outline-none focus:border-yellow-500 text-sm"
                  data-testid="re-edit-notes-input"
                />
                <button 
                  onClick={handleRequestFreeReEdit}
                  disabled={!reEditNotes.trim() || requestingReEdit}
                  className="mt-2 btn-primary w-full md:w-auto"
                  data-testid="request-free-re-edit-btn"
                >
                  {requestingReEdit ? "Submitting..." : "Request Free Re-Edit"}
                </button>
              </div>
            )}

            {/* Paid Re-Edit (after free is used) */}
            {!order.can_request_free_re_edit && order.can_purchase_re_edit && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-semibold">$50</span>
                  <span className="text-sm text-zinc-300">Additional re-edit</span>
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  Your free re-edit has been used. You can purchase additional re-edits for $50 each.
                </p>
                <textarea
                  value={reEditNotes}
                  onChange={(e) => setReEditNotes(e.target.value)}
                  placeholder="Describe the changes you'd like..."
                  className="w-full h-24 bg-zinc-800 border border-zinc-700 rounded-lg p-3 focus:outline-none focus:border-yellow-500 text-sm"
                  data-testid="paid-re-edit-notes-input"
                />
                <button 
                  onClick={handlePurchasePaidReEdit}
                  disabled={!reEditNotes.trim() || requestingReEdit}
                  className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg w-full md:w-auto"
                  data-testid="purchase-re-edit-btn"
                >
                  {requestingReEdit ? "Processing..." : "Purchase Re-Edit ($50)"}
                </button>
              </div>
            )}

            {/* Re-edit count info */}
            {order.re_edit_count > 0 && (
              <p className="text-xs text-zinc-500 mt-4">
                Re-edits used: {order.re_edit_count} (Free: {order.free_re_edit_used ? '1' : '0'}, Paid: {order.paid_re_edits_count || 0})
              </p>
            )}
          </div>
        )}

        {/* Re-Edit History Section */}
        {order.re_edit_history && order.re_edit_history.length > 0 && (
          <div className="card-dark p-4 sm:p-6 mb-4 sm:mb-6" data-testid="re-edit-history-section">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <History size={18} className="text-yellow-500" />
              Re-Edit History
            </h2>
            <div className="space-y-4">
              {order.re_edit_history.map((entry, index) => (
                <div 
                  key={index} 
                  className={`border-l-4 pl-4 py-2 ${
                    entry.status === 'completed' 
                      ? 'border-green-500 bg-green-500/5' 
                      : 'border-yellow-500 bg-yellow-500/5'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Revision #{entry.request_number}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        entry.type === 'paid' 
                          ? 'bg-purple-500/20 text-purple-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {entry.type === 'paid' ? `Paid ($${entry.amount_paid})` : 'Free'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        entry.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {entry.status === 'completed' ? '✓ Completed' : '⏳ In Progress'}
                      </span>
                    </div>
                    <span className="text-xs text-zinc-500">
                      {new Date(entry.requested_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-400 italic">&ldquo;{entry.notes}&rdquo;</p>
                  {entry.completed_at && (
                    <p className="text-xs text-zinc-500 mt-1">
                      Completed: {new Date(entry.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin: Extend Video Retention */}
        {user?.role === "admin" && order.status === "completed" && !order.video_expired && (
          <div className="card-dark p-6 mb-6 border border-zinc-700" data-testid="admin-retention-section">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <Clock size={18} className="text-yellow-500" />
              Admin: Extend Video Retention
            </h2>
            <p className="text-sm text-zinc-400 mb-4">
              Extend the video availability period for this customer (e.g., if they contacted support).
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-zinc-400">Extend by:</label>
                <select
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500"
                  data-testid="extend-days-select"
                >
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
              <button
                onClick={handleExtendRetention}
                disabled={extending}
                className="btn-primary"
                data-testid="extend-retention-btn"
              >
                {extending ? "Extending..." : "Extend Retention"}
              </button>
            </div>
            {order.retention_extended_until && (
              <p className="text-sm text-green-400 mt-3">
                ✓ Retention already extended. New expiry: {new Date(order.retention_extended_until).toLocaleDateString()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};



export default OrderDetailPage;
